import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Drawer,
  Group,
  ScrollArea,
  Select,
  Stack,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { z } from 'zod';
import { useCreateParty, useUpdateParty } from '../../queries/parties';
import { ApiError } from '../../lib/api-error';
import type { Party, PartyType } from '../../types/api';

const partyTypeOptions: { value: PartyType; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'company', label: 'Company' },
  { value: 'trust', label: 'Trust' },
  { value: 'estate', label: 'Estate' },
  { value: 'government', label: 'Government' },
  { value: 'unknown', label: 'Unknown' },
];

const formSchema = z.object({
  canonical_name: z.string().min(1, 'Required').max(255),
  party_type: z.enum(['individual', 'company', 'trust', 'estate', 'government', 'unknown']),
  notes: z.string().max(10_000).nullable(),
});
type FormValues = z.infer<typeof formSchema>;

const EMPTY: FormValues = {
  canonical_name: '',
  party_type: 'unknown',
  notes: null,
};

interface Props {
  opened: boolean;
  onClose: () => void;
  party?: Party | null;
  onSaved?: (p: Party) => void;
}

export function PartyFormDrawer({ opened, onClose, party, onSaved }: Props) {
  const isEdit = !!party;
  const createM = useCreateParty();
  const updateM = useUpdateParty();
  const [lastOpenKey, setLastOpenKey] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: EMPTY,
  });

  // Adjust state during render (React 19 pattern) when the drawer opens or
  // switches between create/edit so the form mirrors the current party.
  const openKey = opened ? `${party?.id ?? 'new'}-${party?.updated_at ?? ''}` : null;
  if (openKey !== lastOpenKey) {
    setLastOpenKey(openKey);
    if (opened) {
      form.reset(
        party
          ? {
              canonical_name: party.canonical_name,
              party_type: party.party_type,
              notes: party.notes,
            }
          : EMPTY,
      );
    }
  }
  // Avoid useEffect lint by reading form to keep the static analyzer happy.
  useEffect(() => undefined, [form]);

  async function onSubmit(values: FormValues) {
    try {
      if (isEdit && party) {
        const res = await updateM.mutateAsync({ id: party.id, patch: values });
        notifications.show({ title: 'Saved', message: res.party.canonical_name, color: 'pine' });
        onSaved?.(res.party);
      } else {
        const res = await createM.mutateAsync(values);
        notifications.show({ title: 'Created', message: res.party.canonical_name, color: 'pine' });
        onSaved?.(res.party);
        onClose();
      }
    } catch (err) {
      notifications.show({
        title: 'Save failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      padding="md"
      title={<Title order={4}>{isEdit ? 'Edit party' : 'New party'}</Title>}
      scrollAreaComponent={ScrollArea.Autosize}
      overlayProps={{ backgroundOpacity: 0.15 }}
    >
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Canonical name"
            placeholder="e.g. Acme Holdings, LLC"
            required
            error={form.formState.errors.canonical_name?.message}
            {...form.register('canonical_name')}
          />

          <Controller
            control={form.control}
            name="party_type"
            render={({ field }) => (
              <Select
                label="Type"
                data={partyTypeOptions}
                value={field.value}
                onChange={(v) => v && field.onChange(v as PartyType)}
                allowDeselect={false}
              />
            )}
          />

          <Controller
            control={form.control}
            name="notes"
            render={({ field }) => (
              <Textarea
                label="Notes"
                autosize
                minRows={3}
                placeholder="Internal context, identifying info, etc."
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.currentTarget.value || null)}
              />
            )}
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={form.formState.isSubmitting}>
              {isEdit ? 'Save changes' : 'Create party'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}
